import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap, timeout, map } from 'rxjs/operators';
import { getCurrentUser } from 'src/app/core/services/functions.service';
import { getEndpoint } from 'src/app/core/services/functions.service';

export interface ExcelRow {
  periodo: string | number;
  c_vend: string;
  valor_meta: string | number;
}

export interface MetaVendedor {
  id?: number;
  periodo: string;
  c_vend: string;
  valor_meta: number;
  c_emp?: string;
  meta_recalculada?: number;
  dias_ausentismos?: number;
  nombre_vendedor?: string;
  vendedor?: {
    codigo_vendedor: string;
    nombre_vendedor: string;
    estado: string;
  };
  usuario_creacion?: string;
  fecha_creacion?: Date;
  usuario_modificacion?: string;
  fecha_modificacion?: Date;
  estado?: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

export interface ResultadoCarga {
  periodo: string;
  c_vend: string;
  valor_meta?: number;
  estado?: string;
  error?: string;
}

export interface ResultadoCargaMasiva {
  exitosos: number;
  errores: number;
  resultados: ResultadoCarga[];
}

export interface BulkLoadResponse {
  registrosInsertados: number;
  registrosEliminados: number;
  registrosFallidos: ResultadoCarga[];
  periodo: string;
  mensaje: string;
}

export interface BulkCreateResponse {
  exitosos: number;
  errores: number;
  resultados: ResultadoCarga[];
}

export interface MetaVendedorFilters {
  periodo?: string;
  searchTerm?: string;
}

@Injectable({
  providedIn: 'root'
})
export class LocalMetaVendedorService {
  //private apiUrl = 'http://127.0.0.1:8000/meta-vendedor';
  ENDPOINT = getEndpoint('coms_meta_vendedor');
  private httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json'
    })
  };

  constructor(private http: HttpClient) { }

  getPaginated(page: number = 1, pageSize: number = 10, company: string, filters?: MetaVendedorFilters): Observable<PaginatedResponse<MetaVendedor>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('page_size', pageSize.toString());

    // Agregar filtros si existen
    if (filters?.periodo) {
      params = params.set('periodo', filters.periodo);
    }
    if (filters?.searchTerm) {
      params = params.set('search', filters.searchTerm);
    }

    return this.http.get<PaginatedResponse<MetaVendedor>>(`${this.ENDPOINT}/paginated/`, { 
      params,
      headers: { 'company': company }
    })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error('Error al cargar los datos paginados'));
        })
      );
  }

  getById(id: number): Observable<MetaVendedor> {
    return this.http.get<MetaVendedor>(`${this.ENDPOINT}/${id}/`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error('Error al cargar los datos de la meta'));
        })
      );
  }

  create(data: MetaVendedor, company: string): Observable<any> {
    const currentUser = getCurrentUser();
    const dataCompleta = {
      ...data,
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: currentUser?.c_emp,
      estado: 'ACTIVO'
    };
    return this.http.post(`${this.ENDPOINT}/`, dataCompleta, {
      headers: { 'company': company }
    }).pipe(
      catchError((error: any) => {
        if (error.error?.detail) {
          throw new Error(error.error.detail);
        }
        throw new Error('Ya existe una meta para este vendedor en el periodo especificado');
      })
    );
  }

  update(id: number, data: MetaVendedor, company: string): Observable<MetaVendedor> {
    const currentUser = getCurrentUser();
    const dataActualizada = {
      ...data,
      usuario_modificacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: currentUser?.c_emp
    };
    
    return this.http.put<MetaVendedor>(`${this.ENDPOINT}/${id}`, dataActualizada, {
      headers: { 'company': company }
    })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          if (error.status === 422) {
            return throwError(() => new Error(error.error?.detail || 'Error de validación en los datos'));
          }
          return throwError(() => new Error(error.error?.detail || 'Error al actualizar los datos'));
        })
      );
  }

  delete(id: number, company: string): Observable<any> {
    return this.http.delete(`${this.ENDPOINT}/${id}/`, {
      headers: { 'company': company }
    })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error('Error al eliminar los datos'));
        })
      );
  }

  deleteByPeriodo(periodo: string, company: string): Observable<number> {
    return this.http.delete<number>(`${this.ENDPOINT}/periodo/${periodo}/`, {
      headers: { 'company': company }
    })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error('Error al eliminar los datos del periodo'));
        })
      );
  }

  bulkLoad(company: string, datos: MetaVendedor[], reemplazar: boolean, periodo?: string): Observable<BulkLoadResponse> {
    const currentUser = getCurrentUser();
    
    // Agregar c_emp y usuario_creacion a cada registro
    const datosCompletos = datos.map(meta => ({
      ...meta,
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: currentUser?.c_emp
    }));

    let url = `${this.ENDPOINT}/bulk`;
    if (reemplazar && periodo) {
      url = `${this.ENDPOINT}/bulk-replace/${periodo}`;
    }

    // Envolver los datos en el formato BulkLoadRequest
    const request = {
      datos: datosCompletos,
      reemplazar: reemplazar,
      periodo: periodo
    };

    return this.http.post<BulkLoadResponse>(url, request, {
      headers: { 'company': company }
    })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error(error.error?.detail || 'Error en la carga masiva'));
        })
      );
  }

  createBulk(metas: MetaVendedor[]): Observable<BulkCreateResponse> {
    const currentUser = getCurrentUser();
    const metasConUsuario = metas.map(meta => ({
      ...meta,
      usuario_creacion: currentUser?.usuario_aplicativo || 'SYSTEM',
      c_emp: currentUser?.c_emp,
      estado: 'ACTIVO'
    }));
    return this.http.post<BulkCreateResponse>(`${this.ENDPOINT}/bulk/`, metasConUsuario, this.httpOptions)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          return throwError(() => new Error('Error al crear múltiples metas'));
        })
      );
  }
}
